import { BarDatum } from "@nivo/bar";
import { FutureEstimatedRenewalCost, FutureEstimatedRenewalCostsView, monthLabels } from "./Dashboard.types";

export type FutureEstimatedRenewalCostsChart = {
    availableYears: number[];
    selectedYear: number;
    showMonths: boolean;
    amounts: number[];
    data: BarDatum[];
    // Nivo collapses its y domain to [0, 0] when nothing has a value, which puts the 0 line in the middle of the
    // chart, so the chart needs an explicit maximum in that case.
    hasNoAmounts: boolean;
};

// A year stored in local storage falls out of the window as soon as the window moves on (a year picked in 2026 is
// gone on new year's day), so the stored year is always resolved against the years the backend actually returned.
export const buildFutureEstimatedRenewalCostsChart = (
    costs: FutureEstimatedRenewalCost[],
    view: FutureEstimatedRenewalCostsView
): FutureEstimatedRenewalCostsChart => {
    const availableYears = costs.map((c) => c.year);
    const fallbackYear = availableYears.length > 0 ? availableYears[0] : view.selectedYear;
    const selectedYear = availableYears.includes(view.selectedYear) ? view.selectedYear : fallbackYear;
    const showMonths = view.mode === "month" && costs.length > 0;

    if (!showMonths) {
        const yearlyAmounts = costs.map((c) => c.estimatedRenewalCosts);
        return {
            availableYears,
            selectedYear,
            showMonths,
            amounts: yearlyAmounts,
            data: costs.map((c) => ({ year: c.year, estimatedRenewalCosts: c.estimatedRenewalCosts })),
            hasNoAmounts: yearlyAmounts.every((amount) => amount === 0),
        };
    }

    const monthlyCosts = costs.find((c) => c.year === selectedYear)?.estimatedRenewalCostsPerMonth;
    const amounts = monthLabels.map((_, monthIndex) => monthlyCosts?.[monthIndex] ?? 0);

    return {
        availableYears,
        selectedYear,
        showMonths,
        amounts,
        data: monthLabels.map((month, monthIndex) => ({ month, estimatedRenewalCosts: amounts[monthIndex] })),
        hasNoAmounts: amounts.every((amount) => amount === 0),
    };
};

export const futureEstimatedRenewalCostsFootnote = (showMonths: boolean, selectedYear: number, currentYear: number) => {
    if (!showMonths) return `*Current year shows estimated renewal costs from today onwards only. Other years show full-year totals.`;
    if (selectedYear === currentYear)
        return `*Shows estimated renewal costs by month for the selected year. Months already passed show no costs, and the current month only includes costs from today onwards.`;
    return `*Shows estimated renewal costs by month for the selected year.`;
};
